import { PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { GWQueryResultStatus } from '../../models/gateway/types';
import { DEFAULT_PAGE_SIZE } from '../../models/news';
import { NewsSubscriptionService } from '../../services/subscription/NewsSubscription.service';
import { SymbolNewsSubscriptionService } from '../../services/subscription/SymbolNewsSubscription.service';
import { loadStock } from '../fundamental';
import { isNewsAvailable } from '../selectors';
import { GetNewsParams } from '../types';
import { RootState } from '..';

import {
  addCallToQueue,
  getLatestNews,
  getNewsForSymbol,
  latestNewsLoadCompleted,
  latestNewsLoadFailed,
  newsForSymbolLoadCompleted,
  newsForSymbolLoadFailed,
  removeCallFromQueue,
  setNewsLoading,
} from './index';

let pageNumber: number = 1;

const getNewsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getLatestNews.type, getNewsForSymbol.type, loadStock.type),
  mergeMap((action: PayloadAction<GetNewsParams>) => {
    const { symbol, page, pageSize, isQueued } = action.payload ?? {};
    const actionType = action.type;
    const isSymbolSpecificRequest = !!symbol && (actionType === getNewsForSymbol.type || actionType === loadStock.type);

    const resultingPageSize = pageSize || DEFAULT_PAGE_SIZE;

    const news = (
      isSymbolSpecificRequest
        ? (state$.value.news.newsForSymbols[symbol] || [])
        : state$.value.news.latestNews
    );

    if (news.length > 0) {
      pageNumber++;
    } else {
      pageNumber = 1;
    }

    const resultingPage = page || pageNumber;
    const hasMore = isNewsAvailable(state$.value, symbol, true);

    const symbolNewsSubscriptionService = SymbolNewsSubscriptionService.getInstance();
    const newsSubscriptionService = NewsSubscriptionService.getInstance();

    return new Observable((observer: Observer<any>) => {
      if (!state$.value.news.isLoading) {
        if (hasMore) {
          observer.next(setNewsLoading(true));
          if (isSymbolSpecificRequest) {
            symbolNewsSubscriptionService.subscribe(symbol, resultingPageSize, resultingPage);
          } else {
            newsSubscriptionService.subscribe();
          }
        } else {
          observer.next(setNewsLoading(false));
        }
      } else if (!isQueued) {
        observer.next(addCallToQueue(action.payload));
      }
    });
  }),
);

const newsLoadCompletedEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(
    latestNewsLoadCompleted.type,
    latestNewsLoadFailed.type,
    newsForSymbolLoadCompleted.type,
    newsForSymbolLoadFailed.type,
  ),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { status } = action.payload;
    const { queuedCalls } = state$.value.news;
    const actionType = action.type;

    if (status === GWQueryResultStatus.READY && queuedCalls.length > 0) {
      let payload = { ...queuedCalls[0] };
      const isSymbolSpecificRequest = !!payload.symbol && (actionType === newsForSymbolLoadCompleted.type || actionType === newsForSymbolLoadFailed.type);
      payload!.isQueued = true;
      let newAction = isSymbolSpecificRequest ? getNewsForSymbol : getLatestNews;

      observer.next(removeCallFromQueue());
      observer.next(newAction(payload));
    }
  })),
);

export default combineEpics(
  getNewsEpic,
  newsLoadCompletedEpic,
);
